// src/components/OrganizationsView/OrganizationsView.js

import React, { useEffect, useState, useMemo } from "react";
import axiosClient from "../../api/axios";
import LinkUpload from "../../api/LinkUpload";
import { Spin } from "antd";
import moment from "moment";

// Import CSS
import './OrganizationListView.css';

// ================= COMPONENT PHÂN TRANG (TỪ MEMBERVIEW) =================
const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    if (totalPages <= 1) {
        return null;
    }

    const handlePageClick = (page) => {
        if (page >= 1 && page <= totalPages) {
            onPageChange(page);
            window.scrollTo(0, 0);
        }
    };

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const startPage = Math.max(1, currentPage - 1);
        const endPage = Math.min(totalPages, currentPage + 1);

        if (startPage > 1) {
            pageNumbers.push(<li key={1}><a className="page-numbers" onClick={() => handlePageClick(1)}>1</a></li>);
            if (startPage > 2) {
                pageNumbers.push(<li key="dots1"><span className="page-numbers dots">…</span></li>);
            }
        }
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(
                <li key={i}>
                    {i === currentPage ? (
                        <span className="page-numbers current">{i}</span>
                    ) : (
                        <a className="page-numbers" onClick={() => handlePageClick(i)}>{i}</a>
                    )}
                </li>
            );
        }
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pageNumbers.push(<li key="dots2"><span className="page-numbers dots">…</span></li>);
            }
            pageNumbers.push(<li key={totalPages}><a className="page-numbers" onClick={() => handlePageClick(totalPages)}>{totalPages}</a></li>);
        }
        return pageNumbers;
    };

    return (
        <nav className="pagination">
            <ul>
                {currentPage > 1 && (<li><a className="prev page-numbers" onClick={() => handlePageClick(currentPage - 1)}>«</a></li>)}
                {renderPageNumbers()}
                {currentPage < totalPages && (<li><a className="next page-numbers" onClick={() => handlePageClick(currentPage + 1)}>»</a></li>)}
            </ul>
        </nav>
    );
};
// ============================================================


const OrganizationsView = () => {
    const token = localStorage.getItem('token');
    const header = { Authorization: `Bearer ${token}` };

    const [allOrganizations, setAllOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;
    
    // Load dữ liệu một lần và lọc trên client
    useEffect(() => {
        setLoading(true);
        axiosClient.post('/Organizations/GetOrganizationsAll', {}, { headers: header })
            .then(res => {
                const sortedData = (res.data || []).sort((a, b) => new Date(a.DateOfEstablishment) - new Date(b.DateOfEstablishment));
                setAllOrganizations(sortedData);
            })
            .catch(err => {
                console.error("Lỗi khi tải dữ liệu:", err);
                setAllOrganizations([]);
            })
            .finally(() => {
                setLoading(false);
            });
        window.scrollTo(0, 0);
    }, []); // Dependency rỗng để chỉ chạy 1 lần

    // Lọc dữ liệu trên client
    const filteredOrganizations = useMemo(() => {
        if (!searchTerm.trim()) {
            return allOrganizations;
        }
        const lowercasedFilter = searchTerm.toLowerCase().trim();
        return allOrganizations.filter(org =>
            org.Name?.toLowerCase().includes(lowercasedFilter)
        );
    }, [searchTerm, allOrganizations]);

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setCurrentPage(1); // Reset về trang 1 khi tìm kiếm
    };

    // Phân trang trên dữ liệu đã lọc
    const currentOrganizationsOnPage = useMemo(() => {
        const firstItemIndex = (currentPage - 1) * itemsPerPage;
        const lastItemIndex = firstItemIndex + itemsPerPage;
        return filteredOrganizations.slice(firstItemIndex, lastItemIndex);
    }, [currentPage, filteredOrganizations]);


    return (
        <div className="container main-body">
            <main>
                <div className="directory-controls">
                    <form className="search-bar" onSubmit={handleSearchSubmit}>
                        <input
                            type="text"
                            placeholder="Tìm tổ chức theo tên..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button type="submit" aria-label="Tìm kiếm">
                            <i className="fa-solid fa-magnifying-glass"></i>
                        </button>
                    </form>
                </div>

                <section>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>
                    ) : filteredOrganizations.length === 0 ? (
                        <div className="no-results" style={{ display: 'block' }}>
                            <i className="fa-solid fa-folder-open"></i>
                            <h4>Không tìm thấy kết quả</h4>
                            <p>Không có tổ chức nào phù hợp với tiêu chí tìm kiếm của bạn.</p>
                        </div>
                    ) : (
                        <div className="organization-grid">
                            {currentOrganizationsOnPage.map(org => {
                                const CardWrapper = org.Link ? 'a' : 'div';
                                const cardProps = org.Link ? { href: org.Link, target: '_blank', rel: 'noopener noreferrer' } : {};
                                const cleanUrl = (url) => url ? url.replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/$/, '') : '';

                                return (
                                    <CardWrapper {...cardProps} className="org-card" key={org.ID}>
                                        <div className="org-card__logo">
                                            {org.Logo ? (
                                                <img src={LinkUpload(org.Logo)} alt={`Logo ${org.Name}`} />
                                            ) : (
                                                <i className="fa-solid fa-building" style={{ fontSize: '2em', color: '#ccc' }}></i>
                                            )}
                                        </div>
                                        <div className="org-card__content">
                                            <h3 className="org-card__name">{org.Name}</h3>
                                            <div className="org-card__info">
                                                {org.Address && <p><i className="fa-solid fa-location-dot"></i> <span>{org.Address}</span></p>}
                                                {org.DateOfEstablishment && <p><i className="fa-solid fa-calendar-days"></i> <span>{moment(org.DateOfEstablishment).format('DD/MM/YYYY')}</span></p>}
                                                {org.Phone && <p><i className="fa-solid fa-phone"></i><a href={`tel:${org.Phone}`} onClick={(e) => e.stopPropagation()}>{org.Phone}</a></p>}
                                                {org.Email && <p><i className="fa-solid fa-envelope"></i><a href={`mailto:${org.Email}`} onClick={(e) => e.stopPropagation()}>{org.Email}</a></p>}
                                                {org.Link && <p><i className="fa-solid fa-globe"></i><a href={org.Link} onClick={(e) => e.stopPropagation()} target="_blank" rel="noopener noreferrer">{cleanUrl(org.Link)}</a></p>}
                                            </div>
                                        </div>
                                    </CardWrapper>
                                );
                            })}
                        </div>
                    )}
                </section>

                {!loading && filteredOrganizations.length > 0 && (
                    <Pagination
                        totalItems={filteredOrganizations.length}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                )}
            </main>
        </div>
    );
};

export default OrganizationsView;